import React, { useMemo } from 'react';
import { ShoppingCart, X, ChevronDown, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '@/services/api';

const ClassificationNode = ({ node, level, isExpanded, toggleNode, handleAddStockItem, stockQuantities, setStockQuantities }) => {
  const expanded = isExpanded(node.id);
  const pl = level === 1 ? 'pl-2' : level === 2 ? 'pl-4' : 'pl-6';
  
  const formatTitle = () => {
    if (level === 1) return node.title.toUpperCase();
    if (node.type === 'Thickness') return `${node.title} Products`;
    if (node.type === 'Profile') return `${node.title} Profile Products`;
    return node.title;
  };

  return (
    <div className="flex flex-col">
      <div 
        className={`flex items-center gap-2 py-2 pr-2 ${pl} cursor-pointer hover:bg-slate-100 transition-colors select-none ${level === 1 ? 'border-b border-slate-200 mt-2' : ''}`}
        onClick={() => toggleNode(node.id)}
      >
        <span className={`text-slate-400 ${expanded ? 'text-[#C91D25]' : ''}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className={`text-xs ${level === 1 ? 'font-bold uppercase tracking-wider text-slate-800' : level === 2 ? 'font-semibold text-slate-700' : 'font-medium text-slate-600'}`}>
          {formatTitle()} {node.type === 'Quality' ? 'Quality' : ''}
        </span>
        <span className="ml-auto text-[10px] text-slate-400 font-medium">
           {node.children.length === 0 ? `(${node.items.length})` : ''}
        </span>
      </div>
      
      {expanded && (
        <div className="flex flex-col">
          {node.children.map(child => (
            <ClassificationNode 
              key={child.id} 
              node={child} 
              level={level + 1} 
              isExpanded={isExpanded} 
              toggleNode={toggleNode} 
              handleAddStockItem={handleAddStockItem} 
              stockQuantities={stockQuantities} 
              setStockQuantities={setStockQuantities} 
            />
          ))}
          
          {node.items.length > 0 && (
            <div className={`flex flex-col gap-2 py-2 pr-2 ${level === 1 ? 'pl-6' : level === 2 ? 'pl-8' : 'pl-10'}`}>
              {node.items.map(stockItem => (
                <div
                  key={stockItem._id}
                  className="p-3 border border-slate-200 rounded-lg hover:border-[#C91D25] hover:shadow-sm transition-all bg-white flex flex-col gap-2 group relative overflow-hidden"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded bg-slate-50 flex items-center justify-center border border-slate-200 shrink-0 text-slate-400 overflow-hidden">
                      {stockItem.item?.image ? (
                        <img 
                          src={stockItem.item.image.startsWith('data:') || stockItem.item.image.startsWith('http') ? stockItem.item.image : `${API_BASE_URL}/api/inventory/settings/images/${stockItem.item.image}`} 
                          alt={stockItem.item?.itemName} 
                          className="w-full h-full object-contain" 
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{ display: stockItem.item?.image ? 'none' : 'flex' }} className="w-full h-full items-center justify-center text-slate-400">
                        <ShoppingCart size={18} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-0.5">
                        <p className="text-sm font-semibold text-slate-800 truncate pr-2" title={stockItem.item?.itemName || 'Unknown Item'}>{stockItem.item?.itemName || 'Unknown Item'}</p>
                        {stockItem.item?.fixingType && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 shrink-0 border border-blue-100 whitespace-nowrap" title={stockItem.item.fixingType}>
                            {stockItem.item.fixingType}
                          </span>
                        )}
                      </div>
                      {(stockItem.item?.height || stockItem.item?.width || stockItem.item?.thickness) && (
                        <div className="flex gap-2 text-[10px] text-slate-600 font-medium mt-1">
                          {stockItem.item?.height && <span><span className="text-slate-400">H:</span> {stockItem.item.height}</span>}
                          {stockItem.item?.width && <span><span className="text-slate-400">W:</span> {stockItem.item.width}</span>}
                          {stockItem.item?.thickness && <span><span className="text-slate-400">T:</span> {stockItem.item.thickness}</span>}
                        </div>
                      )}
                      <div className="flex justify-between items-end mt-1">
                        <div className="text-[10px] text-slate-500 flex flex-col gap-0.5 min-w-0 pr-2">
                          <span className="truncate" title={stockItem.item?.category?.categoryName || 'N/A'}>Cat: {stockItem.item?.category?.categoryName || 'N/A'}</span>
                          <span className="truncate" title={stockItem.store?.locationName || stockItem.store?.name || 'Main Store'}>Loc: {stockItem.store?.locationName || stockItem.store?.name || 'Main Store'}</span>
                        </div>
                        <div className="text-right flex flex-col items-end shrink-0 gap-1">
                          <span className="text-[11px] font-bold text-slate-700">₹{stockItem.purchaseRate || 0}</span>
                          <span className="text-[9px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">Stock: {stockItem.stock}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] font-semibold text-slate-600">Qty:</label>
                      <input
                        type="number"
                        min="1"
                        className="w-14 h-6 text-xs border border-slate-300 rounded px-1.5 outline-none focus:border-[#C91D25]"
                        value={stockQuantities[stockItem._id] || 1}
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value) || 1);
                          setStockQuantities(prev => ({ ...prev, [stockItem._id]: val }));
                        }}
                      />
                    </div>
                    <button
                      className="px-3 py-1 bg-slate-800 hover:bg-[#C91D25] text-white text-[10px] font-semibold rounded transition-colors"
                      onClick={() => handleAddStockItem(stockItem, stockQuantities[stockItem._id])}
                    >
                      ADD
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const EstimationSidebar = ({
  state,
  actions
}) => {
  const {
    catalogType, setCatalogType, catalogFiles, loadingCatalog, selectedPreviewDoc, setSelectedPreviewDoc,
    previewUrl, selectedMainTypes, setSelectedMainTypes, expandedNodes, setExpandedNodes, storeFilter, setStoreFilter,
    storeStock, loadingStore, storeSearchQuery, setStoreSearchQuery, stockQuantities, setStockQuantities, isLocked
  } = state;
  const { handleAddStockItem } = actions;

  const baseFilteredStock = useMemo(() => {
    if (!storeStock) return [];
    return storeStock.filter(s => {
      const isReadyToFix = s.item?.fixingType === 'Ready to Fix';
      if (isReadyToFix && !storeFilter.readyToFix) return false;
      if (!isReadyToFix && !storeFilter.looseItems) return false;
      return true;
    });
  }, [storeStock, storeFilter]);

  const availableMainTypes = useMemo(() => {
    return [...new Set(baseFilteredStock.map(s => s.item?.category?.categoryName).filter(Boolean))].sort();
  }, [baseFilteredStock]);

  const classificationTree = useMemo(() => {
    if (!baseFilteredStock || baseFilteredStock.length === 0) return [];
    
    let filteredStock = baseFilteredStock;


    if (storeSearchQuery) {
      const q = storeSearchQuery.toLowerCase();
      filteredStock = filteredStock.filter(s => (s.item?.itemName || '').toLowerCase().includes(q));
    }

    if (!selectedMainTypes || selectedMainTypes.length === 0) {
      return [];
    }
    
    filteredStock = filteredStock.filter(s => selectedMainTypes.includes(s.item?.category?.categoryName));

    const tree = [];
    const categories = [...new Set(filteredStock.map(s => s.item?.category?.categoryName).filter(Boolean))].sort();
    
    categories.forEach(cat => {
      const catItems = filteredStock.filter(s => s.item?.category?.categoryName === cat);
      const catNode = { id: `cat-${cat}`, title: cat, type: 'Category', children: [], items: [] };
      
      const qualities = [...new Set(catItems.map(s => s.item?.quality || s.item?.brand?.brandName || 'Standard').filter(Boolean))].sort();
      
      qualities.forEach(qual => {
        const qualItems = catItems.filter(s => (s.item?.quality || s.item?.brand?.brandName || 'Standard') === qual);
        const qualNode = { id: `cat-${cat}-qual-${qual}`, title: qual, type: 'Quality', children: [], items: [] };
        
        const hasThickness = qualItems.some(s => s.item?.thickness);
        const hasProfile = qualItems.some(s => s.item?.profile);
        const hasFixingType = qualItems.some(s => s.item?.fixingType);
        
        let dynamicField = null;
        if (hasThickness) dynamicField = 'thickness';
        else if (hasProfile) dynamicField = 'profile';
        else if (hasFixingType) dynamicField = 'fixingType';
        
        if (dynamicField) {
          const dynamicValues = [...new Set(qualItems.map(s => s.item?.[dynamicField] || 'Other').filter(Boolean))].sort();
          dynamicValues.forEach(val => {
            const valItems = qualItems.filter(s => (s.item?.[dynamicField] || 'Other') === val);
            const valNode = { 
              id: `cat-${cat}-qual-${qual}-dyn-${val}`, 
              title: val, 
              type: dynamicField === 'thickness' ? 'Thickness' : dynamicField === 'profile' ? 'Profile' : 'Fixing Type', 
              children: [], 
              items: valItems 
            };
            qualNode.children.push(valNode);
          });
        } else {
          qualNode.items = qualItems;
        }
        
        catNode.children.push(qualNode);
      });
      
      tree.push(catNode);
    });
    
    return tree;
  }, [storeStock, selectedMainTypes, storeSearchQuery]);

  const isExpanded = (id) => {
    if (storeSearchQuery) return true; // Auto-expand on search
    if (expandedNodes[id] !== undefined) return expandedNodes[id];
    // Default: keep Level 1 (Category) and Level 2 (Quality) expanded
    const depth = id.split('-').length;
    return depth <= 4; // 'cat-Name' is length 2, 'cat-Name-qual-Name' is length 4
  };

  const toggleNode = (id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: isExpanded(id) ? false : true }));
  };

  return (
    <div className={`w-full xl:w-[350px] shrink-0 bg-white rounded-xl flex flex-col overflow-hidden h-[500px] xl:h-auto xl:min-h-[650px] ${isLocked ? 'pointer-events-none' : 'pointer-events-auto'}`}>
      <div className="p-4 border-b border-slate-200 flex flex-col gap-3 shrink-0">
        <div className="flex items-center gap-2 border border-slate-300 rounded px-3 py-1.5 w-full bg-white">
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
          <select
            value={catalogType}
            onChange={(e) => setCatalogType(e.target.value)}
            className="outline-none text-xs font-semibold text-slate-700 bg-transparent cursor-pointer border-none focus:ring-0"
          >
            <option value="Catalog">Catalog</option>
            <option value="Store">Store</option>
          </select>
          <div className="w-px h-4 bg-slate-300 mx-1"></div>
          <input
            type="text"
            placeholder={catalogType === 'Store' ? "Search entire product catalog..." : `Search ${catalogType.toLowerCase()} items...`}
            className="outline-none text-xs w-full bg-transparent placeholder-slate-400 text-slate-800"
            value={catalogType === 'Store' ? storeSearchQuery : ''}
            onChange={(e) => {
              if (catalogType === 'Store') {
                setStoreSearchQuery(e.target.value);
              }
            }}
          />
        </div>

        {catalogType === 'Store' && (
          <div className="flex flex-col gap-3 pl-1 pt-1">
            <div className="flex gap-4 items-center">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="fixingType"
                  checked={storeFilter.readyToFix}
                  onChange={() => setStoreFilter({ readyToFix: true, looseItems: false })}
                  className="w-3.5 h-3.5 text-[#C91D25] border-slate-300 focus:ring-[#C91D25] cursor-pointer"
                />
                <span className="text-[11px] font-medium text-slate-600">Ready to Fix</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="fixingType"
                  checked={storeFilter.looseItems}
                  onChange={() => setStoreFilter({ readyToFix: false, looseItems: true })}
                  className="w-3.5 h-3.5 text-[#C91D25] border-slate-300 focus:ring-[#C91D25] cursor-pointer"
                />
                <span className="text-[11px] font-medium text-slate-600">Loose Items</span>
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              {availableMainTypes.map(type => (
                <label key={type} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedMainTypes.includes(type)}
                    onChange={() => {
                      setSelectedMainTypes(prev => {
                        if (prev.includes(type)) return prev.filter(t => t !== type);
                        return [...prev, type];
                      });
                    }}
                    className="w-3.5 h-3.5 text-[#C91D25] rounded border-slate-300 focus:ring-[#C91D25] cursor-pointer"
                  />
                  <span className="text-[11px] font-semibold text-slate-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedPreviewDoc ? (
        <div className="flex flex-col h-[600px] xl:h-full bg-slate-50">
          <div className="p-4 shrink-0">
            <div className="p-3 border border-[#C91D25] rounded-lg shadow-sm bg-white flex items-start gap-3 relative">
              <div className="w-10 h-10 rounded bg-white flex items-center justify-center border border-[#C91D25] text-[#C91D25] shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              </div>
              <div className="flex-1 min-w-0 pr-6">
                <p className="text-sm font-semibold text-slate-800 truncate">{selectedPreviewDoc.title || selectedPreviewDoc.fileName}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{selectedPreviewDoc.category || 'Document'}</p>
              </div>
              <button
                onClick={() => setSelectedPreviewDoc(null)}
                className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 rounded transition-colors pointer-events-auto"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden min-h-[400px]">
            {previewUrl ? (
              <iframe
                src={`${previewUrl}#toolbar=0`}
                className="w-full h-full border-none"
                title="Catalog Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <div className="w-5 h-5 border-2 border-slate-300 border-t-[#C91D25] rounded-full animate-spin mb-2"></div>
                <span className="text-xs font-medium">Loading preview...</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {loadingCatalog ? (
            <div className="flex flex-col items-center justify-center text-slate-400 py-20">
              <p className="font-medium text-sm">Loading...</p>
            </div>
          ) : catalogFiles.length > 0 && catalogType === 'Catalog' ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {catalogFiles.map(file => (
                <div
                  key={file._id}
                  className="p-3 border border-slate-200 rounded-lg hover:border-[#C91D25] hover:shadow-sm transition-all cursor-pointer bg-slate-50 flex items-start gap-3 group"
                  onClick={() => setSelectedPreviewDoc(file)}
                >
                  <div className="w-10 h-10 rounded bg-white flex items-center justify-center border border-slate-200 shrink-0 text-slate-400 group-hover:text-[#C91D25] group-hover:border-[#C91D25]">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-[#C91D25] transition-colors" title={file.title || file.fileName}>{file.title || file.fileName}</p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate" title={file.category || 'Document'}>{file.category || 'Document'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : catalogType === 'Store' ? (
            <div className="flex-1 overflow-y-auto pb-4 custom-scrollbar bg-slate-50/50">
              {loadingStore ? (
                <div className="flex flex-col items-center justify-center text-slate-400 py-20">
                  <div className="w-5 h-5 border-2 border-slate-300 border-t-[#C91D25] rounded-full animate-spin mb-2"></div>
                  <span className="text-xs font-medium">Loading catalog...</span>
                </div>
              ) : classificationTree.length > 0 ? (
                classificationTree.map(node => (
                  <ClassificationNode 
                    key={node.id} 
                    node={node} 
                    level={1} 
                    isExpanded={isExpanded} 
                    toggleNode={toggleNode} 
                    handleAddStockItem={handleAddStockItem}
                    stockQuantities={stockQuantities}
                    setStockQuantities={setStockQuantities}
                  />
                ))
              ) : selectedMainTypes.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-slate-400 py-20">
                  <div className="w-16 h-16 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-4 shadow-sm">
                    <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
                  </div>
                  <p className="font-medium text-sm text-slate-500">Select a main product type to view products</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 py-20">
                  <div className="w-16 h-16 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-4 shadow-sm">
                    <ShoppingCart className="w-8 h-8 text-slate-300" />
                  </div>
                  <p className="font-medium text-sm text-slate-500">No products match your criteria</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-slate-400 py-20">
              <div className="w-16 h-16 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              </div>
              <p className="font-medium text-sm">No {catalogType.toLowerCase()} items found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

